import type { CalculateInputCalculatorService } from '~/types/calculator-service-types'
import type { CalculatorLocalInputCart, CartProductsLocalWithPrices, CartState } from '~/types/local-types'
import { DiscountType } from '@food-ticket/calculator/src/input/types/Discount'
import { SourceType } from '@food-ticket/calculator/src/input/types/Input'
import cloneDeep from 'lodash.clonedeep'
import debounce from 'lodash.debounce'
import isEqual from 'lodash.isequal'
import mapKeys from 'lodash.mapkeys'
import { calculateTip } from '~/composables/calculateTip'
import { calculatorCall } from '~/composables/calculatorCall'
import {
    cleanCalculatorResponse,
    convertCartProductsForCalculator,
    convertGqlCategoriesForCalculator,
    convertGqlDispatchTypeForCalculator,
    convertGqlExtraItemsForCalculator,
    convertGqlExtrasForCalculator,
    convertGqlProductsForCalculator,
    convertGqlZipCodesForCalculator
} from '~/composables/calculatorConvertData'
import { productCanBeInCart } from '~/composables/productCanBeInCart'
import { calculateQuery } from '~/graphql/queries/calculate.query.gql'
import { checkout } from '~/store/checkout'
import { shop } from '~/store/shop'
import { webshopSettings } from '~/store/webshopSettings'
import { PaymentMethodCalculatorService } from '~/types/calculator-service-types'
import { PaymentMethodOrderBuddy } from '~/types/orderbuddy-types'
import { eventTracker } from '~/utils/event-tracker'
import { cleanProduct, getCartProductId } from '~/utils/product-utils'

export const cart = defineStore('cart', {
    state: (): CartState => ({
        cartIsLoading: false,
        cartItemsShouldAnimate: true,
        cart: {
            products: {},
            free_products: [],
            discounts: []
        },
        calculatorInputLastValidated: {},
        cartPricesLocal: {},
        cartPricesValidated: {},
        calculatorInputDatabase: {},
        devInfo: {
            localPayload: {},
            localLog: [],
            validatedPayload: {},
            validatedLog: []
        }
    } as CartState),
    persist: {
        storage: piniaPluginPersistedstate.localStorage()
    },
    getters: {
        getCartProducts (): CartProductsLocalWithPrices {
            const currentCart = cloneDeep(this.cart.products)
            const targetCart: CartProductsLocalWithPrices = {}
            Object.keys(currentCart).forEach(key => {
                targetCart[key] = {
                    ...this.cart.products[key],
                    priceLocal: this.cartPricesLocal?.cart?.products[key]?.total,
                    priceVerified: this.cartPricesValidated?.cart?.products[key]?.total
                }
            })
            return targetCart
        },
        getCartQuantity (): number {
            return Object.values(this.getCartProducts).reduce((total: number, product: any) => {
                return total + product.quantity
            }, 0)
        },
        hasCartProducts (): boolean {
            return Object.keys(this.getCartProducts).length > 0
        },
        /** Total price getters */
        getCartTotalPriceLocal (): number {
            return this?.cartPricesLocal?.total || 0
        },
        getCartTotalPriceValidated (): number {
            return this?.cartPricesValidated?.total || 0
        },
        getCartTotalPrice (): number {
            return this?.getCartTotalPriceValidated ? this.getCartTotalPriceValidated : this.getCartTotalPriceLocal
        },
        /** Sub price getters */
        getCartSubPriceLocal (): number {
            return this?.cartPricesLocal?.subtotal || 0
        },
        getCartSubPriceValidated (): number {
            return this?.cartPricesValidated?.subtotal || 0
        },
        /** Discount price getters */
        getCartDiscountedPrice (): number {
            return this?.getCartSubPriceValidated ? (this.getCartSubPriceValidated - (this?.cartPricesValidated?.discount || 0)) : this.getCartSubPriceLocal
        },
        getAdditionalCostsLocal (): any {
            return Object.fromEntries(
                Object.entries(this?.cartPricesLocal?.costs || {})
            )
        },
        getAdditionalCostsVerified (): any {
            return Object.fromEntries(
                Object.entries(this?.cartPricesValidated?.costs || {})
            )
        },
        hasAlchoholInCart (): boolean {
            return Object.values(this.cart.products).some((product: any) => shop().findProduct(product.id)!.hasAlcohol || false)
        },
        productAlreadyInCart () {
            return (product: any) => {
                const productWithoutQuantity: any = cleanProduct(cloneDeep(product))
                delete productWithoutQuantity.quantity
                return Object.values(this.cart.products).find((cartProduct: any) => {
                    const cartProductWithoutQuantity: any = cleanProduct(cloneDeep(cartProduct))
                    delete cartProductWithoutQuantity.quantity
                    return isEqual(cartProductWithoutQuantity, productWithoutQuantity) ? cleanProduct(cartProduct) : false
                })
            }
        },
        getTip (): number {
            return checkout().input.tipData.tipRadio === 'custom' ? checkout().input.tipData.customTip : calculateTip(cart().getCartSubPriceLocal, checkout().input.tipData.tipRadio)
        }
    },
    actions: {
        addProduct (product: any) {
            if (!this.cartIsLoading) {
                const cartProductId = getCartProductId(product)
                if (this.productAlreadyInCart(product)) {
                    // TODO: make this aware of the minimum quantity of a product
                    this.cart.products[cartProductId].quantity = Number(this.cart.products[cartProductId].quantity) + Number(product.quantity)
                } else {
                    const { $event } = useNuxtApp()
                    $event('before-new-product-added-to-cart')
                    eventTracker('addProductFromCart', product)
                    this.cart.products[cartProductId] = cleanProduct(product)
                    this.updateLocalCartPrices()
                }
            }
        },
        updateProduct (product: any, index: number | string) {
            this.cartItemsShouldAnimate = false
            if (this.productAlreadyInCart(product)) {
                // TODO: when modifying a product, its position in the cart can change, this should be fixed
                this.deleteProduct(product, index)
                this.addProduct(product)
            } else {
                if (Object.prototype.hasOwnProperty.call(this.cart.products, index)) {
                    const newProductId = getCartProductId(product)
                    this.cart.products = mapKeys(this.cart.products, (_value, key) => {
                        if (key === index) {
                            return newProductId
                        }
                        return key
                    })
                    this.cart.products[newProductId] = cleanProduct(product)
                }
            }
            this.updateLocalCartPrices()
            setTimeout(() => {
                this.cartItemsShouldAnimate = true
            }, 300) // this should be the same as the transition duration in the cart
        },
        async deleteProduct (product: object, index: number | string) {
            if (!this.cartIsLoading) {
                eventTracker('deleteProductFromCart', product)
                delete this.cart.products[index]
                await nextTick()
                if (Object.keys(this.cart.products).length === 0) {
                    this.resetCart()
                }
            }
        },
        async removeUnavailableCartProducts () {
            const cartProductKeys = Object.keys(this.getCartProducts)
            await Promise.all(cartProductKeys.map(async key => {
                const cartProduct = this.getCartProducts[key]
                if (!productCanBeInCart(cartProduct, checkout().input.dispatchType)) {
                    this.deleteProduct(cartProduct, key)
                }
            }))
        },
        setCalculatorInputDatabase () {
            this.calculatorInputDatabase = cloneDeep({
                is_loaded_from_order: false,
                language: 'NL',
                source: SourceType.ONLINE,
                cart: {
                    schedule: {
                        time: undefined,
                        date: '',
                        asap: false
                    },
                    packaging_costs_override: null,
                    free_products: []
                },
                client: {
                    id: Number.parseInt(webshopSettings()?.global?.client?.id || '0')
                },
                categories: convertGqlCategoriesForCalculator(shop().currentMenu),
                products: convertGqlProductsForCalculator(shop().allProducts),
                extras: convertGqlExtrasForCalculator(shop().allExtras),
                items: convertGqlExtraItemsForCalculator(shop().allExtrasItems),
                discounts: {
                    [DiscountType.SIMPLE]: [],
                    [DiscountType.DISCOUNTED_PRODUCT]: [],
                    [DiscountType.FREE_PRODUCT]: []
                },
                settings: [
                    {
                        setting: 'delivery_costs',
                        value: (webshopSettings()?.global?.delivery?.deliveryCosts ?? 0).toString()
                    },
                    {
                        setting: 'delivery_min',
                        value: (webshopSettings()?.global?.delivery?.minimumOrderThreshold ?? 0).toString()
                    },
                    {
                        setting: 'delivery_free',
                        value: (webshopSettings()?.global?.delivery?.freeDeliveryThreshold ?? 0).toString()
                    },
                    {
                        setting: 'vat_default_rate',
                        value: (webshopSettings()?.global?.vatDefaultRate ?? 0).toString()
                    },
                    {
                        setting: 'delivery_costs_percat',
                        value: (webshopSettings()?.global?.delivery?.deliveryCostsPerCategory || 0).toString()
                    },
                    {
                        setting: 'admin_price_ideal',
                        value: (webshopSettings().global?.paymentCosts?.ideal ?? 0).toString()
                    }
                ],
                vat_rates: [ 0, 9, 21 ]
            })
        },
        updateLocalCartPrices: debounce(async () => {
            await cart().calculateLocalCartPrices()
        }, 300),
        async calculateLocalCartPrices () {
            if (this.hasCartProducts) {
                const { public: { cartDebugMode } } = useRuntimeConfig()
                const calculatorInputCart: CalculatorLocalInputCart = {
                    zip_codes: convertGqlZipCodesForCalculator(webshopSettings()?.global?.delivery?.deliveryAreas || []),
                    cart: {
                        zip_code: checkout().input.customer.addressGroup.postalCode ?? '',
                        tip: this.getTip,
                        payment_method: checkout()?.input?.payment?.method ? (checkout().input.payment.method).toLowerCase() : 'cash',
                        type: convertGqlDispatchTypeForCalculator(checkout().input.dispatchType),
                        products: convertCartProductsForCalculator(cloneDeep(this.cart.products)) // this needs to be done via cloneDeep else the cart will be modified
                    }
                }
                const localCalculatorInput = {
                    ...this.calculatorInputDatabase,
                    ...calculatorInputCart
                }
                if (cartDebugMode) {
                    this.devInfo.localPayload = localCalculatorInput
                }
                // See https://foodticket.atlassian.net/browse/CV-4 as to why we have to clone the input object here
                const localCalculatorData: any = await calculateLocal(cloneDeep(localCalculatorInput))
                if (localCalculatorData?._log) {
                    if (cartDebugMode) {
                        this.devInfo.localLog = localCalculatorData._log
                    }
                    delete localCalculatorData._log
                } else {
                    if (cartDebugMode) {
                        this.devInfo.localCalculatorData = []
                    }
                }
                this.cartPricesLocal = cleanCalculatorResponse(localCalculatorData)
                // If the cash amount is lower than the total price, reset the cash amount
                if (checkout().input.payment.cashAmount! < this.getCartTotalPriceLocal) {
                    checkout().resetCashPayment()
                }
            } else {
                this.cartPricesLocal = {}
            }
        },
        async updateValidatedCartPrices () {
            if (this.hasCartProducts) {
                const { public: { cartDebugMode } } = useRuntimeConfig()
                const calculatorInput: CalculateInputCalculatorService = {
                    clientUuid: webshopSettings()?.global?.client?.uuid?.toString(),
                    source: SourceType.ONLINE,
                    cart: {
                        free_products: [],
                        tip: this.getTip,
                        payment_method: checkout()?.input?.payment?.method === PaymentMethodOrderBuddy.IDEAL ? PaymentMethodCalculatorService.IDEAL : PaymentMethodCalculatorService.CASH,
                        discounts: this?.cart?.discounts?.map((discount: string) => ({
                            code: discount,
                            type: 'code'
                        })),
                        zip_code: checkout().input.customer.addressGroup.postalCode,
                        // @ts-expect-error see TODOs below
                        products: convertCartProductsForCalculator(cloneDeep(this.cart.products)), // TODO: Fix TS issue here. Need to add discount_id and perhaps other props from ProductInputCalculatorService, this may improve how discounts work
                        type: convertGqlDispatchTypeForCalculator(checkout().input.dispatchType)
                    }
                }
                if (!isEqual(this.calculatorInputLastValidated, calculatorInput)) {
                    this.cartIsLoading = true
                    if (cartDebugMode) {
                        this.devInfo.validatedPayload = calculatorInput
                    }
                    const fetchedCalculatorData = await calculatorCall(calculateQuery, 'calculate', {
                        input: calculatorInput
                    }, false)
                    if (fetchedCalculatorData?._log) {
                        if (cartDebugMode) {
                            this.devInfo.validatedLog = fetchedCalculatorData._log
                        }
                        delete fetchedCalculatorData._log
                    } else {
                        if (cartDebugMode) {
                            this.devInfo.validatedLog = []
                        }
                    }
                    this.cartPricesValidated = cleanCalculatorResponse(fetchedCalculatorData)
                    this.calculatorInputLastValidated = calculatorInput
                    this.cartIsLoading = false
                }
            } else {
                this.cartPricesValidated = {}
            }
        },
        async addDiscountCode (discountCode: string) {
            this.cart.discounts[0] = discountCode
            await cart().updateValidatedCartPrices()
        },
        async removeDiscountCode (discountCode: string) {
            this.cart.discounts = this.cart.discounts.filter((code: string) => code !== discountCode)
            await cart().updateValidatedCartPrices()
        },
        resetCart () {
            this.cart.products = {}
            this.cartPricesLocal = {}
            this.cartPricesValidated = {}
            this.calculatorInputLastValidated = {}
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(cart, import.meta.hot))
}
